import { getQuestionTitle, renderAnswer } from "./answer-util";
import { CalendarDate } from "./calendar-date";
import { CrmAuthorId } from "./constants";
import { nullEntityId, parseEntityIdSafe } from "./id-parser";
import { QuestionType } from "./question";
import { currencyInCentsToString, formatCurrency, formatNumber } from "./util/currency";
import { formatDateNoTime, formatDateTimeInZone, formatMonthAndDay, formattedDateTimeDefault, } from "./util/date";
import { Maybe } from "./util/maybe";
import { formatPhoneNumber } from "./util/phone-formatting";
export var BridgeFieldFormatter;
(function (BridgeFieldFormatter) {
    BridgeFieldFormatter["Type"] = "Type";
    BridgeFieldFormatter["Percentage"] = "Percentage";
    BridgeFieldFormatter["Computed"] = "Computed";
    BridgeFieldFormatter["CurrencyInCents"] = "CurrencyInCents";
    BridgeFieldFormatter["DateAndTime"] = "DateAndTime";
})(BridgeFieldFormatter || (BridgeFieldFormatter = {}));
export function getRawValue(entity, fieldName) {
    const getRawValueActual = (entity, fieldNames) => {
        if (fieldNames.length === 0) {
            return Maybe.none();
        }
        const currentFieldName = fieldNames[0];
        if (typeof entity === "object" &&
            !!entity &&
            typeof entity[currentFieldName] !== "undefined") {
            if (fieldNames.length === 1) {
                return Maybe.fromValue(entity[currentFieldName]);
            }
            return getRawValueActual(entity[currentFieldName], fieldNames.slice(1));
        }
        return Maybe.none();
    };
    return getRawValueActual(entity, fieldName.split("."));
}
const formatDateValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    if (rawValue.hasValue) {
        const value = rawValue.get();
        if (value && (typeof value === "string" || typeof value === "object")) {
            return formatDateNoTime(value);
        }
    }
    return "";
};
const formatDateTimeValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    if (rawValue.hasValue) {
        const value = rawValue.get();
        if (value && (typeof value === "string" || typeof value === "object")) {
            return formattedDateTimeDefault(value);
        }
    }
    return "";
};
const formatTextValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse("");
    if (!value) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value.toString === "function") {
        return value.toString();
    }
};
const formatPhoneValue = (entity, fieldName) => {
    const textValue = formatTextValue(entity, fieldName);
    return formatPhoneNumber(textValue);
};
const formatPercentageValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value || !(typeof value === "string" || typeof value === "number")) {
        return "";
    }
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    if (isNaN(parsed) && typeof value === "string") {
        return value;
    }
    return `${formatNumber(parsed)}%`;
};
const formatCurrencyValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value || !(typeof value === "string" || typeof value === "number")) {
        return "";
    }
    const parsed = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(parsed) && typeof value === "string") {
        return value;
    }
    return formatCurrency(value);
};
const formatNumberValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (typeof value === "string") {
        const parsed = value.includes(".") ? parseFloat(value) : parseInt(value, 10);
        if (!isNaN(parsed)) {
            return formatNumber(parsed);
        }
    }
    if (typeof value === "number") {
        return formatNumber(value);
    }
    return "";
};
const formatNaryValue = (entity, fieldName, naryValues) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value || typeof value !== "string") {
        return "";
    }
    const naryValue = naryValues.find(nv => nv.value === value);
    if (naryValue) {
        return naryValue.label;
    }
    return value;
};
const formatNaryArrayValue = (entity, fieldName, naryValues) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value || !Array.isArray(value)) {
        return "";
    }
    const labels = value
        .map(v => {
        const naryValue = naryValues.find(nv => nv.value === v);
        return naryValue ? naryValue.label : v;
    })
        .filter(Boolean);
    return labels.join(", ");
};
const formatRegionValue = (entity, fieldName, regions) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value || typeof value !== "string") {
        return "";
    }
    const region = regions.find(r => r.id === value);
    if (region) {
        return region.name;
    }
    return value;
};
const formatUserValue = (entity, fieldName, users, productName) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value || typeof value !== "string" || value === nullEntityId()) {
        return "";
    }
    if (value === CrmAuthorId) {
        return productName;
    }
    const user = users[value];
    if (user) {
        return user.name || user.email || user.id;
    }
    return value;
};
const formatCustomListValue = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    const value = rawValue.getOrElse(null);
    if (!value) {
        return "";
    }
    if (typeof value !== "object") {
        return value;
    }
    return formatTextValue(value, "name");
};
const formatComputedCommunityPrice = (entity) => {
    const basePrice = formatCurrencyValue(entity, "monthlyPrice");
    const highPrice = formatCurrencyValue(entity, "priceHighEnd");
    if (basePrice && highPrice) {
        return `${basePrice} - ${highPrice}`;
    }
    return basePrice || highPrice || "";
};
const formatOverrideableDateTimeValue = (entity, metadata) => {
    const actual = getRawValue(entity, metadata.name).getOrElse(null);
    let override;
    if (metadata.dependsOn && metadata.dependsOn.length > 0) {
        override = metadata.dependsOn
            .map(dep => getRawValue(entity, dep).getOrElse(null))
            .find(val => val !== null);
    }
    if (override) {
        return formatDateNoTime(override);
    }
    if (actual) {
        return formatDateNoTime(actual);
    }
    return "";
};
export const formatCompositeDueDateTime = (entity) => {
    const dueDate = getRawValue(entity, "dueDate").getOrElse(null);
    const dueDateTime = getRawValue(entity, "dueDateTime").getOrElse(null);
    if (dueDate) {
        return formatDateNoTime(dueDate);
    }
    if (dueDateTime) {
        return formattedDateTimeDefault(dueDateTime);
    }
    return "";
};
export const formatCompositeDueDateTimeWithTimezone = (entity, timezone) => {
    const dueDate = getRawValue(entity, "dueDate").getOrElse(null);
    const dueDateTime = getRawValue(entity, "dueDateTime").getOrElse(null);
    if (dueDate) {
        return formatDateNoTime(dueDate);
    }
    if (dueDateTime) {
        return formatDateTimeInZone(new Date(dueDateTime), timezone);
    }
    return "";
};
const formatBirthdayValue = (entity, birthdayFieldName, isFakeFieldName) => {
    const isBirthdayFake = getRawValue(entity, isFakeFieldName).getOrElse(false);
    const birthday = getRawValue(entity, birthdayFieldName).getOrElse(null);
    if (birthday) {
        const dateString = formatDateNoTime(birthday);
        if (isBirthdayFake) {
            const parsed = CalendarDate.parse(dateString);
            if (parsed.hasValue) {
                const year = parsed.get().year.toString();
                return `~${year}`;
            }
        }
        else {
            return dateString;
        }
    }
    return "";
};
const formatCurrencyInCents = (entity, fieldName) => {
    const rawValue = getRawValue(entity, fieldName);
    if (rawValue.hasValue) {
        const value = rawValue.get();
        let numberValue = NaN;
        if (typeof value === "number") {
            numberValue = value;
        }
        if (typeof value === "string") {
            numberValue = parseInt(value, 10);
        }
        if (!isNaN(numberValue)) {
            return formatCurrency(currencyInCentsToString(numberValue));
        }
    }
    return "";
};
export const formatEntityValue = (entity, fieldName, metadata, tenantInfo) => {
    const fieldParts = fieldName?.split(".") || [];
    if (fieldParts.length > 0) {
        const last = fieldParts[fieldParts.length - 1];
        if (metadata.aliases?.includes(last)) {
            fieldParts[fieldParts.length - 1] = metadata.name;
            fieldName = fieldParts.join(".");
        }
    }
    switch (metadata.formatter) {
        case BridgeFieldFormatter.CurrencyInCents: {
            return formatCurrencyInCents(entity, fieldName);
        }
        case BridgeFieldFormatter.Percentage: {
            return formatPercentageValue(entity, fieldName);
        }
        case BridgeFieldFormatter.DateAndTime: {
            return formatDateTimeValue(entity, fieldName);
        }
        case BridgeFieldFormatter.Type: {
            switch (metadata.questionType) {
                case QuestionType.date: {
                    return formatDateValue(entity, fieldName);
                }
                case QuestionType.calendarDateNoYear: {
                    return formatMonthAndDay(entity, fieldName);
                }
                case QuestionType.number: {
                    return formatNumberValue(entity, fieldName);
                }
                case QuestionType.currency: {
                    return formatCurrencyValue(entity, fieldName);
                }
                case QuestionType.nary: {
                    return formatNaryValue(entity, fieldName, metadata.naryValues || []);
                }
                case QuestionType.naryArray: {
                    return formatNaryArrayValue(entity, fieldName, metadata.naryValues || []);
                }
                case QuestionType.region: {
                    return formatRegionValue(entity, fieldName, tenantInfo.regions);
                }
                case QuestionType.user: {
                    return formatUserValue(entity, fieldName, tenantInfo.users, tenantInfo.productName);
                }
                case QuestionType.customList: {
                    return formatCustomListValue(entity, fieldName);
                }
                case QuestionType.phone: {
                    return formatPhoneValue(entity, fieldName);
                }
                default: {
                    return formatTextValue(entity, fieldName);
                }
            }
        }
        case BridgeFieldFormatter.Computed: {
            const fieldPath = fieldName.split(".");
            fieldPath.pop();
            const parentFieldName = fieldPath.join(".");
            let parentEntity = entity;
            if (parentFieldName) {
                parentEntity = getRawValue(entity, parentFieldName).getOrElse(null);
            }
            switch (metadata.name) {
                case "dateAdded": {
                    return formatOverrideableDateTimeValue(parentEntity, metadata);
                }
                case "__computed__monthlyPriceRange": {
                    return formatComputedCommunityPrice(parentEntity);
                }
                case "compositeDueDateTime": {
                    return formatCompositeDueDateTime(parentEntity);
                }
                case "birthday": {
                    return formatBirthdayValue(parentEntity, "birthday", "isBirthdayFake");
                }
                case "additionalClientBirthday": {
                    return formatBirthdayValue(parentEntity, "additionalClientBirthday", "additionalClientIsBirthdayFake");
                }
                default: {
                    return formatTextValue(entity, fieldName);
                }
            }
        }
        default: {
            return formatTextValue(entity, fieldName);
        }
    }
};
export const withCustomReportFields = (entities) => entities.map(entity => ({
    ...entity,
    fields: [...entity.fields, ...entity.customReportFields],
}));
export const getRelationDisplayPath = (entityMetadata, relationName, entityMetadataList) => {
    const relation = getEntityRelation(entityMetadata, relationName);
    const otherEntity = entityMetadataList.find(e => e.name === relation?.otherEntityName);
    if (otherEntity?.preferredDisplay) {
        if (otherEntity.preferredDisplay.fieldName) {
            return {
                [relationName]: otherEntity.preferredDisplay.fieldName,
            };
        }
        if (otherEntity.preferredDisplay.relation?.name) {
            return {
                [relationName]: getRelationDisplayPath(otherEntity, otherEntity.preferredDisplay.relation.name, entityMetadataList),
            };
        }
    }
    return { [relationName]: "id" };
};
export const getRelationFieldName = (entityMetadata, relationName, entityMetadataList) => {
    const displayPath = getRelationDisplayPath(entityMetadata, relationName, entityMetadataList);
    const segments = [];
    let current = displayPath;
    while (typeof current === "object" && !!current) {
        const key = Object.keys(current)[0];
        segments.push(key);
        current = current[key];
    }
    segments.push(current);
    return segments.join(".");
};
export const formatEntityRelationDisplay = (entity, relationName, entityMetadata, entityMetadataList, tenantInfo) => {
    if (!entity) {
        return "";
    }
    const displayPath = getRelationDisplayPath(entityMetadata, relationName, entityMetadataList);
    const relation = getEntityRelation(entityMetadata, relationName);
    const otherEntityMetadata = entityMetadataList.find(e => e.name === relation?.otherEntityName);
    if (!relation || !otherEntityMetadata) {
        return "";
    }
    if (typeof displayPath[relationName] === "string") {
        const otherEntityField = getEntityField(otherEntityMetadata, displayPath[relationName]);
        if (otherEntityField) {
            return formatEntityValue(entity[relationName], displayPath[relationName], otherEntityField, tenantInfo);
        }
        return "";
    }
    if (typeof displayPath[relationName] === "object") {
        const otherEntity = entity[relationName];
        return formatEntityRelationDisplay(otherEntity, Object.keys(displayPath[relationName])[0], otherEntityMetadata, entityMetadataList, tenantInfo);
    }
    return "";
};
export const getEntityField = (entityMetadata, fieldName) => {
    const field = entityMetadata?.fields.find(f => f.name === fieldName || f.aliases?.includes(fieldName));
    return field ?? null;
};
export const getEntityRelation = (entityMetadata, relationName) => {
    const relation = entityMetadata?.relations.find(r => r.name === relationName || r.aliases?.includes(relationName));
    return relation ?? null;
};
export const findRelatedField = (field, entityMetadata, entityMetadataList) => {
    const helper = (remainingSegments, columnTitleSegments, entity) => {
        if (remainingSegments.length === 1) {
            const field = getEntityField(entity, remainingSegments[0]);
            if (field) {
                return {
                    title: [...columnTitleSegments, field.title].join(": "),
                    metadata: field,
                    entityMetadata: entity,
                };
            }
            return undefined;
        }
        const relation = getEntityRelation(entity, remainingSegments[0]);
        const otherEntity = entityMetadataList.find(e => e.name === relation?.otherEntityName);
        if (relation && otherEntity) {
            return helper(remainingSegments.slice(1), [...columnTitleSegments, relation.title], otherEntity);
        }
        return undefined;
    };
    const result = helper(field.split("."), [], entityMetadata);
    const priorityField = entityMetadata.priorityFields?.find(f => f.fieldName === field);
    if (result && priorityField?.title) {
        return {
            ...result,
            title: priorityField.title,
        };
    }
    return result;
};
export const isRelatedFieldName = (fieldName) => {
    if (!fieldName) {
        return false;
    }
    return fieldName.includes(".");
};
export const isRelatedField = (select) => {
    if (!select) {
        return false;
    }
    if (!select.fieldName) {
        return false;
    }
    return isRelatedFieldName(select.fieldName);
};
export const getHeaderTitlesForSelect = (select, entityMetadata, entityMetadataList, questions) => {
    const result = [];
    entityMetadata.identifierFields.forEach(f => {
        const idField = getEntityField(entityMetadata, f);
        if (idField) {
            result.push(idField.title);
        }
    });
    select.forEach(s => {
        const questionField = getQuestionFieldFromSelect(s, entityMetadata, entityMetadataList);
        if (questionField) {
            const question = questions.find(q => q.id === questionField.questionId);
            if (question) {
                const questionTitle = getQuestionTitle(question) || questionField.questionId;
                const title = `${questionField.titlePrefix}${questionTitle}`;
                result.push(title);
            }
        }
        else if (s.fieldName && isRelatedField(s)) {
            const matchingFieldInfo = findRelatedField(s.fieldName, entityMetadata, entityMetadataList);
            if (!matchingFieldInfo) {
                throw new Error(`Could not find field ${s.fieldName} in entity`);
            }
            if (matchingFieldInfo) {
                result.push(matchingFieldInfo.title);
            }
        }
        else if (s.fieldName) {
            const matchingField = getEntityField(entityMetadata, s.fieldName);
            if (matchingField) {
                result.push(matchingField.title);
            }
        }
    });
    return result;
};
export const entityToPlain = (rawEntity, select, entityMetadata, entityMetadataList, questions, tenantInfo) => {
    const result = {};
    const entity = rawEntity;
    entityMetadata.identifierFields.forEach(f => {
        const idField = getEntityField(entityMetadata, f);
        if (idField) {
            result[idField.title] = formatEntityValue(entity, f, idField, tenantInfo);
        }
    });
    select.forEach(s => {
        const questionField = getQuestionFieldFromSelect(s, entityMetadata, entityMetadataList);
        if (questionField) {
            const question = questions.find(q => q.id === questionField.questionId);
            let answerEntity = entity;
            if (questionField.accessor !== ".") {
                answerEntity = getRawValue(entity, questionField.accessor).getOrElse(null);
            }
            const entityAnswers = (answerEntity ? answerEntity["answersField"] || [] : []);
            const answer = entityAnswers.find(a => a.questionId === questionField.questionId);
            if (question) {
                const questionTitle = question.title || questionField.questionId;
                const title = `${questionField.titlePrefix}${questionTitle}`;
                result[title] = answer
                    ? renderAnswer(question, answer, tenantInfo.users)
                    : "";
            }
        }
        else if (s.fieldName && isRelatedField(s)) {
            const matchingFieldInfo = findRelatedField(s.fieldName, entityMetadata, entityMetadataList);
            if (!matchingFieldInfo) {
                throw new Error(`Could not find field ${s.fieldName} in entity ${entityMetadata.name}`);
            }
            if (matchingFieldInfo) {
                result[matchingFieldInfo.title] = formatEntityValue(entity, s.fieldName, matchingFieldInfo.metadata, tenantInfo);
            }
        }
        else if (s.fieldName) {
            const matchingField = getEntityField(entityMetadata, s.fieldName);
            if (matchingField) {
                result[matchingField.title] = formatEntityValue(entity, s.fieldName, matchingField, tenantInfo);
            }
        }
    });
    return result;
};
export const QUESTION_FIELD_IDENTIFIER = "__question__";
/**
 * Takes a select and, if it is a select for a question field, it will
 * return the questionId, entityMetadata that question rests on, the
 * accessor path to the related entity that holds the question,
 * and the title prefix to prepend to the question title
 *
 * Some examples:
 *  - { fieldName: "name" } => null
 *  - { fieldName: "community.name" } => null
 *  - { fieldName: "community.__question__:123" } =>
 *          {
 *            questionId: 123,
 *            entityMetadata: Community,
 *            accessor: "community",
 *            titlePrefix: "Community"
 *          }
 *  - { fieldName: "referralContact.referenceBusiness.__question__:234" } =>
 *          {
 *            questionId: 456,
 *            entityMetadata: ReferenceBusiness,
 *            accessor: "referralContact.referenceBusiness",
 *            titlePrefix: "Referral Contact: Organization"
 *          }
 *   - { questionId: 789 } =>
 *          {
 *           questionId: 789,
 *           entityMetadata: Client,
 *           accessor: ".",
 *           titlePrefix: ""
 *          }
 *    - { fieldName: "__question__:789" } =>
 *          {
 *           questionId: 789,
 *           entityMetadata: Client,
 *           accessor: ".",
 *           titlePrefix: ""
 *          }
 *
 *
 * @param select desired field from the entityMetadata
 * @param entityMetadata The root entityMetadata the select is based off of
 * @param entityMetadataList The set of entityMetdatas that nested relations can use
 * @returns a QuestionFieldMetadata that holds the information described above.
 */
export const getQuestionFieldFromSelect = (select, entityMetadata, entityMetadataList) => {
    if (select.questionId) {
        return {
            questionId: select.questionId,
            entityMetadata,
            accessor: ".",
            titlePrefix: "",
        };
    }
    if (select.fieldName) {
        const relationParts = select.fieldName.split(".");
        const last = relationParts[relationParts.length - 1];
        const questionParts = last.split(":");
        if (questionParts.length === 2 &&
            questionParts[0] === QUESTION_FIELD_IDENTIFIER) {
            const maybeQuestionId = parseEntityIdSafe(questionParts[1]);
            const questionId = maybeQuestionId.getOrElse(nullEntityId());
            if (questionId !== nullEntityId()) {
                const relationPartsWithoutField = relationParts.slice(0, -1);
                const titleParts = [];
                let currentEntityMetadata = entityMetadata;
                for (const relationPart of relationPartsWithoutField) {
                    const relation = getEntityRelation(currentEntityMetadata, relationPart);
                    const otherEntityMetadata = entityMetadataList.find(e => e.name === relation?.otherEntityName);
                    if (!relation || !otherEntityMetadata) {
                        return null;
                    }
                    titleParts.push(relation.title);
                    currentEntityMetadata = otherEntityMetadata;
                }
                return {
                    questionId,
                    entityMetadata: currentEntityMetadata,
                    accessor: relationPartsWithoutField.length === 0
                        ? "."
                        : relationPartsWithoutField.join("."),
                    titlePrefix: titleParts.length === 0 ? "" : `${titleParts.join(": ")}: `,
                };
            }
        }
    }
    return null;
};
export const getQuestionFieldFromSort = (sort, entityMetadata, entityMetadataList) => {
    if (!sort) {
        return null;
    }
    return getQuestionFieldFromSelect({ fieldName: sort.column, questionId: sort.questionId }, entityMetadata, entityMetadataList);
};
export const getQuestionFieldsGroupedByRelation = (select, bridgeType, bridgeTypes) => {
    return select
        .map(select => {
        return getQuestionFieldFromSelect(select, bridgeType, bridgeTypes);
    })
        .reduce((map, q) => {
        if (!q) {
            return map;
        }
        if (!map[q.accessor]) {
            map[q.accessor] = [];
        }
        map[q.accessor].push(q);
        return map;
    }, {});
};
export var AdvancedSearchAggregateOperator;
(function (AdvancedSearchAggregateOperator) {
    AdvancedSearchAggregateOperator["Count"] = "Count";
    AdvancedSearchAggregateOperator["Sum"] = "Sum";
    AdvancedSearchAggregateOperator["Average"] = "Average";
    AdvancedSearchAggregateOperator["Min"] = "Min";
    AdvancedSearchAggregateOperator["Max"] = "Max";
})(AdvancedSearchAggregateOperator || (AdvancedSearchAggregateOperator = {}));
export const getAggregateFieldTitle = (field, operator) => {
    let title;
    if (field?.title) {
        switch (operator) {
            case AdvancedSearchAggregateOperator.Count: {
                title = `Has ${field.title}`;
                break;
            }
            case AdvancedSearchAggregateOperator.Min: {
                switch (field.questionType) {
                    case QuestionType.text:
                    case QuestionType.nary: {
                        title = `${field.title} - First`;
                        break;
                    }
                    case QuestionType.date: {
                        title = `${field.title} - Earliest`;
                        break;
                    }
                    case QuestionType.currency:
                    case QuestionType.number: {
                        title = `${field.title} - Minimum`;
                        break;
                    }
                    default: {
                        title = field.title;
                        break;
                    }
                }
                break;
            }
            case AdvancedSearchAggregateOperator.Max: {
                switch (field.questionType) {
                    case QuestionType.text:
                    case QuestionType.nary: {
                        title = `${field.title} - Last`;
                        break;
                    }
                    case QuestionType.date: {
                        title = `${field.title} - Latest`;
                        break;
                    }
                    case QuestionType.currency:
                    case QuestionType.number: {
                        title = `${field.title} - Maxiumum`;
                        break;
                    }
                    default: {
                        title = field.title;
                        break;
                    }
                }
                break;
            }
            case AdvancedSearchAggregateOperator.Sum: {
                title = `${field.title} - Total`;
                break;
            }
            case AdvancedSearchAggregateOperator.Average: {
                title = `${field.title} - Average`;
                break;
            }
            default: {
                title = field.title;
                break;
            }
        }
    }
    else {
        title = "Count";
    }
    return title;
};
//# sourceMappingURL=bridge.js.map